/**
 * DM W8-1a (plan c6412 K1) — the typed inbox item: a `pm_question` row with a `kind`, a per-kind
 * `payload` and a structured `resolution`. One zod source for the api (ask/list/close doors) and the
 * web inbox. `payload.action.args` is kept out of the search document, NOT out of the read projection
 * (GET /questions, dm.question.list): the producer redacts secrets before asking (plan c6412 K6).
 */
import { z } from 'zod';
import { DM_GRANT_TYPES } from './dmGrant.js';
import { DM_INBOX_EXECUTOR_DOORS } from './dmInboxExecutors.js';
export const DM_INBOX_KINDS = ['question', 'decision', 'permission', 'approval', 'info'];
/** `pm_question.status` — the api table's enum is this array (plan K1 keeps it three-valued). */
export const DM_INBOX_STATUSES = ['open', 'answered', 'withdrawn'];
/** `info` never blocks `requires_no_open_questions`; every other kind does while open. */
export const DM_INBOX_NON_BLOCKING_KINDS = ['info'];
/** W8-2 (c6475) — the kinds the text answer door takes; permission/approval/info resolve only through the resolve door. */
export const DM_INBOX_ANSWERABLE_KINDS = ['question', 'decision'];
export const DM_INBOX_SOURCES = ['agent', 'hook', 'job', 'kernel', 'human'];
export const DM_INBOX_CLOSED_REASONS = [
    'withdrawn',
    'closed_by_human',
    'expired',
    'superseded',
    'auto_resolved',
];
export const DM_INBOX_BLAST_RADII = ['low', 'medium', 'high'];
/** W8-1b — the store's closed set (`dmGrant.ts`), one source. */
export const DM_INBOX_GRANT_TYPES = DM_GRANT_TYPES;
export const DM_INBOX_GRANT_TTLS = ['once', 'slice', 'wave'];
export const DM_INBOX_SORTS = ['oldest', 'newest', 'priority'];
/**
 * W8b-3 — the question list's default when `sort` is absent (tool, REST door, service).
 * The web inbox default is separate (`dmInboxUrl.ts`) and always sends `sort` explicitly (c6896).
 */
export const DM_INBOX_DEFAULT_SORT = 'newest';
export const DM_INBOX_CONTEXT_MAX_LENGTH = 4000;
export const DM_INBOX_OPTIONS_MAX = 8;
export const DM_INBOX_NOTE_MAX_LENGTH = 2000;
/** Serialized payload ceiling; the column is jsonb, the bound keeps one row from carrying a dump. */
export const DM_INBOX_PAYLOAD_MAX_BYTES = 16_384;
const keySchema = z.string().regex(/^[a-z0-9][a-z0-9_.-]{0,63}$/, 'a lowercase key (a-z 0-9 _ . -, ≤64)');
const sha40Schema = z.string().regex(/^[0-9a-f]{40}$/, 'a full 40-hex sha');
const sha256Schema = z.string().regex(/^[0-9a-f]{64}$/, 'a 64-hex sha256');
const idSchema = z.number().int().positive();
export const dmInboxOptionSchema = z
    .object({
    key: keySchema,
    label: z.string().min(1).max(200),
    effect: z.string().min(1).max(500),
    recommended: z.boolean().optional(),
})
    .strict();
const policyKeySchema = z.string().regex(/^[a-z0-9][a-z0-9_.:-]{0,127}$/);
export const dmInboxLinksSchema = z
    .object({
    itemId: idSchema.optional(),
    waveItemId: idSchema.optional(),
    pr: idSchema.optional(),
    sha: sha40Schema.optional(),
    policyKey: policyKeySchema.optional(),
    /** W8d-2b — the `[operator]` comment a pre-issued (wave-form / bundled) permission was opened on. */
    operatorRequestCommentId: idSchema.optional(),
})
    .strict();
export const dmInboxGrantRequestSchema = z
    .object({
    type: z.enum(DM_INBOX_GRANT_TYPES),
    scope: z
        .object({
        slice_key: keySchema.optional(),
        wave: idSchema.optional(),
        pr: idSchema.optional(),
        command_class: keySchema.optional(),
        command_sha256: sha256Schema.optional(),
        // W8-2 — a merge request names the head; a model_override request names the model.
        sha: sha40Schema.optional(),
        job_id: idSchema.optional(),
        model: keySchema.optional(),
    })
        .strict(),
    ttl: z.enum(DM_INBOX_GRANT_TTLS),
    /** W8d-4 — a command_class of a `check.class_grantable` policy: one use (the resolve door enforces it either way). */
    max_uses: z.literal(1).optional(),
})
    .strict();
const MAX_USES_CLASS_ONLY = 'max_uses is a command_class request\'s (a class_grantable policy: one use)';
/** The ask shape: door + draft args only. `draft_sha256` is server-owned, so a client value is refused (strict). */
export const dmInboxActionSchema = z
    .object({
    // W8-2 — the executor's closed set; the api parses `args` against the door's own schema.
    door: z.enum(DM_INBOX_EXECUTOR_DOORS),
    args: z.record(z.string(), z.unknown()),
})
    .strict();
/** The stored/read shape: the ask door adds `draft_sha256` = sha256 of the canonical `args` (W8-2 re-checks it). */
const dmInboxStoredActionSchema = dmInboxActionSchema.extend({ draft_sha256: sha256Schema }).strict();
const commonPayloadShape = {
    context: z.string().max(DM_INBOX_CONTEXT_MAX_LENGTH).optional(),
    /** #3902 — the guard's redacted command summary in `context` was cut at its cap. */
    summary_truncated: z.boolean().optional(),
    blast_radius: z.enum(DM_INBOX_BLAST_RADII).optional(),
    links: dmInboxLinksSchema.optional(),
};
const optionsSchema = z.array(dmInboxOptionSchema).max(DM_INBOX_OPTIONS_MAX);
/** Option keys unique; `default_on_timeout` names one of them. */
function optionsConsistent(p, ctx) {
    const keys = (p.options ?? []).map((o) => o.key);
    if (new Set(keys).size !== keys.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['options'], message: 'option keys must be unique' });
    }
    if (p.default_on_timeout !== undefined && !keys.includes(p.default_on_timeout)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['default_on_timeout'],
            message: 'default_on_timeout must name one of the options',
        });
    }
}
export const dmInboxQuestionPayloadSchema = z
    .object({ ...commonPayloadShape, options: optionsSchema.optional(), default_on_timeout: keySchema.optional() })
    .strict()
    .superRefine(optionsConsistent);
export const dmInboxDecisionPayloadSchema = z
    .object({ ...commonPayloadShape, options: optionsSchema.min(2), default_on_timeout: keySchema.optional() })
    .strict()
    .superRefine(optionsConsistent);
/** W8d-2b — the most rows one bundled permission item carries. */
export const DM_INBOX_PERMISSION_BUNDLE_MAX = 12;
/** The types a bundle row may name: no `command_once` (a bundle has no once form). */
export const DM_INBOX_BUNDLE_GRANT_TYPES = ['merge', 'command_class', 'sudo', 'model_override'];
/**
 * W8d-2b — one row of a bundle: always the work-unit form (`scope.wave`, `ttl: 'wave'`); a command class row
 * names its policy in `scope.policy_key` (the singular ask's `links.policyKey`). Per type, exactly its fields.
 */
export const dmInboxBundleGrantRequestSchema = z
    .object({
    type: z.enum(DM_INBOX_BUNDLE_GRANT_TYPES),
    scope: z
        .object({ wave: idSchema, command_class: keySchema.optional(), policy_key: policyKeySchema.optional(), model: keySchema.optional() })
        .strict(),
    ttl: z.literal('wave'),
    max_uses: z.literal(1).optional(),
})
    .strict()
    .superRefine((row, ctx) => {
    if (row.max_uses !== undefined && row.type !== 'command_class') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['max_uses'], message: MAX_USES_CLASS_ONLY });
    }
    const need = {
        merge: [],
        command_class: ['command_class', 'policy_key'],
        sudo: ['command_class'],
        model_override: ['model'],
    };
    for (const key of ['command_class', 'policy_key', 'model']) {
        const wanted = need[row.type].includes(key);
        if (wanted !== (row.scope[key] !== undefined)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['scope', key],
                message: wanted ? `a ${row.type} bundle row names scope.${key}` : `a ${row.type} bundle row does not take scope.${key}`,
            });
        }
    }
});
/** W8d-2b — exactly one of `grant_request` (one ask) and `grant_requests` (a bundle; its links name no row's unit). */
export const dmInboxPermissionPayloadSchema = z
    .object({
    ...commonPayloadShape,
    grant_request: dmInboxGrantRequestSchema.optional(),
    grant_requests: z.array(dmInboxBundleGrantRequestSchema).min(1).max(DM_INBOX_PERMISSION_BUNDLE_MAX).optional(),
})
    .strict()
    .superRefine((p, ctx) => {
    if ((p.grant_request === undefined) === (p.grant_requests === undefined)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['grant_request'], message: 'a permission names exactly one of grant_request and grant_requests' });
    }
    if (p.grant_request?.max_uses !== undefined && p.grant_request.type !== 'command_class') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['grant_request', 'max_uses'], message: MAX_USES_CLASS_ONLY });
    }
    if (p.grant_requests === undefined)
        return;
    for (const key of ['waveItemId', 'pr', 'sha', 'policyKey']) {
        if (p.links?.[key] !== undefined) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['links', key], message: `a bundle names links.${key} per row (scope), not on the item` });
        }
    }
});
/**
 * W8-5b — the ONE wave reader (resolve door, list row, detail): `grant_request.scope.wave` first, then
 * `links.waveItemId`. Read defensively (any payload, parsed or raw); an unreadable id is `null`.
 * W8d-2b — a bundle (`grant_requests`) has no single unit: `null` (each row names its own).
 */
export function dmInboxWaveItemIdOf(payload) {
    const bag = (v) => typeof v === 'object' && v !== null && !Array.isArray(v) ? v : null;
    const isId = (v) => typeof v === 'number' && Number.isInteger(v) && v > 0;
    const p = bag(payload);
    if (p?.grant_requests !== undefined)
        return null;
    const scopeWave = bag(bag(p?.grant_request)?.scope)?.wave;
    if (isId(scopeWave))
        return scopeWave;
    const linkWave = bag(p?.links)?.waveItemId;
    return isId(linkWave) ? linkWave : null;
}
export const dmInboxApprovalPayloadSchema = z.object({ ...commonPayloadShape, action: dmInboxActionSchema }).strict();
/** An approval row as stored and read: the action carries the server's `draft_sha256`. */
export const dmInboxStoredApprovalPayloadSchema = z
    .object({ ...commonPayloadShape, action: dmInboxStoredActionSchema })
    .strict();
export const dmInboxInfoPayloadSchema = z.object(commonPayloadShape).strict();
const withinPayloadCap = (p) => p === null || p === undefined || JSON.stringify(p).length <= DM_INBOX_PAYLOAD_MAX_BYTES;
const capMessage = { message: `payload exceeds ${DM_INBOX_PAYLOAD_MAX_BYTES} serialized characters`, path: ['payload'] };
/**
 * The ask shape: `kind` discriminates which payload schema applies. `question`/`info` may omit the
 * payload (legacy plain text); the three acting kinds must carry theirs.
 */
export const dmInboxItemSchema = z
    .discriminatedUnion('kind', [
    z.object({ kind: z.literal('question'), payload: dmInboxQuestionPayloadSchema.nullable().optional() }).strict(),
    z.object({ kind: z.literal('decision'), payload: dmInboxDecisionPayloadSchema }).strict(),
    z.object({ kind: z.literal('permission'), payload: dmInboxPermissionPayloadSchema }).strict(),
    z.object({ kind: z.literal('approval'), payload: dmInboxApprovalPayloadSchema }).strict(),
    z.object({ kind: z.literal('info'), payload: dmInboxInfoPayloadSchema.nullable().optional() }).strict(),
])
    .refine((v) => withinPayloadCap(v.payload), capMessage);
export const DM_INBOX_PERMISSION_DECISIONS = ['allow_once', 'allow_wave', 'deny'];
export const DM_INBOX_APPROVAL_DECISIONS = ['approve', 'reject'];
/** Structured resolution. W8-1a writes `note` (human close); the resolve door (W8-2) the rest. */
export const dmInboxResolutionSchema = z
    .object({
    decision: z.enum([...DM_INBOX_PERMISSION_DECISIONS, ...DM_INBOX_APPROVAL_DECISIONS]).optional(),
    option: keySchema.optional(),
    note: z.string().min(1).max(DM_INBOX_NOTE_MAX_LENGTH).optional(),
    grant_ids: z.array(idSchema).max(50).optional(),
    executed: z
        .object({ door: z.string().min(1).max(64), ok: z.boolean(), ref: z.string().max(200).optional() })
        .strict()
        .optional(),
})
    .strict();
/** `POST /api/dm/questions/:id/close` body. */
export const dmInboxCloseBodySchema = z
    .object({ note: z.string().min(1).max(DM_INBOX_NOTE_MAX_LENGTH).optional() })
    .strict();
/** The key the human close door charges (pinned equal to the api constant). */
export const DM_QUESTION_CLOSE_DOOR_KEY = 'pm.question.close';
/** The key the resolve door charges (pinned equal to the api constant). */
export const DM_QUESTION_RESOLVE_DOOR_KEY = 'pm.question.resolve';
export const DM_INBOX_ANSWER_MAX_LENGTH = 4000;
const noteSchema = z.string().trim().min(1).max(DM_INBOX_NOTE_MAX_LENGTH);
/**
 * `POST /api/dm/questions/:id/resolve` body: the kind the person saw (a mismatch is refused) and that kind's choice.
 * An approval echoes the `draft_sha256` it displayed; `permissions` selects a plan approval's grants (K5).
 */
export const dmInboxResolveBodySchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('question'), answer: z.string().trim().min(1).max(DM_INBOX_ANSWER_MAX_LENGTH) }).strict(),
    z.object({ kind: z.literal('decision'), option: keySchema, note: noteSchema.optional() }).strict(),
    z
        .object({
        kind: z.literal('permission'),
        decision: z.enum(DM_INBOX_PERMISSION_DECISIONS),
        // W8d-2b #4032 — the work unit ids the person saw, row by row (the detail's `grantUnits`); the door compares.
        workUnitIds: z.array(idSchema.nullable()).max(DM_INBOX_PERMISSION_BUNDLE_MAX).optional(),
        note: noteSchema.optional(),
    })
        .strict(),
    z
        .object({
        kind: z.literal('approval'),
        decision: z.enum(DM_INBOX_APPROVAL_DECISIONS),
        draftSha256: sha256Schema,
        permissions: z.array(keySchema).max(50).optional(),
        note: noteSchema.optional(),
    })
        .strict(),
    z.object({ kind: z.literal('info'), note: noteSchema.optional() }).strict(),
]);
/** `resolved` = the person's choice took effect; `superseded` = the draft moved, the item closed and nothing ran. */
export const DM_INBOX_RESOLVE_OUTCOMES = ['resolved', 'superseded'];
//# sourceMappingURL=dmInbox.js.map